import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { PortfolioKeys } from "web_myplann/Api/Portfolio/portfolioKeys";
import {
	getPortfolioIdAlertBoxesV2,
	type PortfolioAlertBoxListResponseDTO,
	type PortfolioEventTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { processResponse } from "web_myplann/Infrastructure/Api/ApiUtils";

/**
 * Event Alert boxes for a contract. The backend applies eligibility and dismissal
 * server-side; pass `bypassDismissal` to also return temporarily dismissed alerts
 * (used when arriving from the Notification Center).
 *
 * `eventType` + `eventDate` target one specific event the client opened from the
 * Notification Center: the backend then returns its alert box even when the
 * event falls outside the normal visibility window (the centre's horizon reaches
 * further). Other alerts still follow the standard rules.
 */
export const usePortfolioAlertBoxesQuery = (
	id: number,
	bypassDismissal = false,
	enabled = true,
	eventType?: PortfolioEventTypeEnum,
	eventDate?: string,
) => {
	return useQuery({
		queryKey: PortfolioKeys.alertBoxes(
			id,
			bypassDismissal,
			eventType,
			eventDate,
		),
		queryFn: processResponse(() =>
			getPortfolioIdAlertBoxesV2(id, bypassDismissal, eventType, eventDate),
		),
		enabled,
		// Keep the current alerts visible while switching the bypassDismissal
		// variant (e.g. after a dismiss), so the stack does not flash empty.
		placeholderData: keepPreviousData,
		select: (data: PortfolioAlertBoxListResponseDTO | undefined) =>
			data?.alerts ?? [],
	});
};
